/**
 * AdminPage — Administration surface host.
 *
 * Renders AdminMenu in the left sidebar slot (mutually exclusive with CategorySidebar).
 * Routes:
 *  /admin              → placeholder (Tenants active, coming cycle 3+)
 *  /admin/sub-clients  → SubClientTile grid with 4 mock entries per brief §2.3
 *
 * Role gate (vertical-ref mode): renders a 403-style notice for users without
 * core-developer or vertical-product-owner role.
 *
 * Refs: marketplace-broker-c2 brief §3 stage 4, §2.3 (mock data)
 */

import { useConfig } from '../config/ConfigContext';
import { useAuth } from '../auth/KeycloakProvider';
import AdminMenu, { type AdminRoute } from '../components/AdminMenu';
import SubClientTile, { type SubClientData } from '../components/SubClientTile';
import type { Route } from '../shell/Router';

// Mock sub-client data per brief §2.3
const MOCK_SUB_CLIENTS: SubClientData[] = [
  {
    slug: 'acme-capital',
    name: 'Acme Capital',
    context: 'European mid-market PE fund. Securitization pilot, Deal vertical.',
    lifecycle: 'provisioned',
  },
  {
    slug: 'nordic-fund',
    name: 'Nordic Fund Partners',
    context: 'Nordic infrastructure debt. Active on all Deal modules.',
    lifecycle: 'provisioned',
  },
  {
    slug: 'alpine-investments',
    name: 'Alpine Investments',
    context: 'Swiss family office. Onboarding in progress.',
    lifecycle: 'pending',
  },
  {
    slug: 'legacy-trust',
    name: 'Legacy Trust AG',
    context: 'Account suspended — pending compliance review.',
    lifecycle: 'suspended',
  },
];

interface AdminPageProps {
  activePath: Route;
  onNavigate: (path: Route) => void;
}

// ── Role gate ─────────────────────────────────────────────────────────────────

function AdminAccessDenied() {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-12)',
      }}
    >
      <p className="dp-eyebrow">Access denied</p>
      <h2>Administration requires elevated access</h2>
      <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
        Your account needs the <code>core-developer</code> or{' '}
        <code>vertical-product-owner</code> role to access this surface.
      </p>
    </div>
  );
}

// ── Sub-clients view ──────────────────────────────────────────────────────────

function SubClientsView() {
  return (
    <div className="dp-shell__content">
      <div style={{ marginBottom: 'var(--sp-6)' }}>
        <p className="dp-eyebrow">Sub-clients</p>
        <h2 style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>Managed tenants</h2>
        <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
          Sub-client management is in early preview. Full lifecycle controls ship in cycle 3+.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 'var(--sp-5)',
        }}
      >
        {MOCK_SUB_CLIENTS.map((sc) => (
          <SubClientTile
            key={sc.slug}
            data={sc}
            onManage={(slug) => {
              // Placeholder — full sub-client detail route in cycle 3
              console.log('[AdminPage] Manage sub-client:', slug);
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Placeholder view ──────────────────────────────────────────────────────────

function AdminPlaceholderView({ section }: { section: string }) {
  return (
    <div
      className="dp-shell__content"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-12)',
        color: 'var(--fg-3)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <p className="dp-eyebrow">{section}</p>
      <p style={{ fontSize: 'var(--fs-body-sm)' }}>
        This section is a placeholder — implementation planned for cycle 3+.
      </p>
    </div>
  );
}

// ── AdminPage ─────────────────────────────────────────────────────────────────

export default function AdminPage({ activePath, onNavigate }: AdminPageProps) {
  const config = useConfig();
  const { hasRole } = useAuth();

  // In vertical-ref mode, gate to privileged roles
  const isVerticalRef = config.instance.mode === 'vertical-ref';
  const canAccess =
    !isVerticalRef || hasRole('core-developer') || hasRole('vertical-product-owner');

  function handleAdminNav(path: AdminRoute) {
    onNavigate(path as Route);
  }

  return (
    <>
      <AdminMenu
        activePath={activePath}
        onNavigate={handleAdminNav}
      />

      {!canAccess ? (
        <AdminAccessDenied />
      ) : activePath === '/admin/sub-clients' ? (
        <SubClientsView />
      ) : (
        <AdminPlaceholderView section="Tenants" />
      )}
    </>
  );
}
